// Servicio de Stock
// Gestiona el stock de productos por depósito

import { getCollection, createDocument, updateDocument, getDocument } from './firestore.service'
import { Timestamp } from 'firebase/firestore'
import { buildProductStockSummary } from '../utils/stock'

const STOCK_COLLECTION = 'stock'
const STOCK_MOVEMENTS_COLLECTION = 'stockMovements'

/**
 * Obtener stock de un producto en un depósito específico
 * @param {string} productId
 * @param {string} warehouseId
 * @param {Object} options
 * @returns {Promise<Object|null>}
 */
export const getStock = async (productId, warehouseId, options = {}) => {
  try {
    const filters = [
      { field: 'productId', operator: '==', value: productId },
      { field: 'warehouseId', operator: '==', value: warehouseId }
    ]
    if (options.size) {
      filters.push({ field: 'size', operator: '==', value: options.size })
    }
    if (options.color) {
      filters.push({ field: 'color', operator: '==', value: options.color })
    }
    const stockList = await getCollection(STOCK_COLLECTION, filters)
    
    return stockList.length > 0 ? stockList[0] : null
  } catch (error) {
    console.error('Error al obtener stock:', error)
    throw error
  }
}

/**
 * Obtener todo el stock de un producto (en todos los depósitos)
 * @param {string} productId
 * @returns {Promise<Array>}
 */
export const getProductStock = async (productId) => {
  try {
    return await getCollection(STOCK_COLLECTION, [
      { field: 'productId', operator: '==', value: productId }
    ], 'warehouseId', 'asc')
  } catch (error) {
    console.error('Error al obtener stock del producto:', error)
    throw error
  }
}

export const getProductStockSummary = async (productId) => {
  const items = await getProductStock(productId)
  return buildProductStockSummary(items)
}

/**
 * Obtener todo el stock de un depósito
 * @param {string} warehouseId
 * @returns {Promise<Array>}
 */
export const getWarehouseStock = async (warehouseId) => {
  try {
    // Obtener stock con filtro y ordenar en el cliente para evitar índices compuestos
    const stock = await getCollection(STOCK_COLLECTION, [
      { field: 'warehouseId', operator: '==', value: warehouseId }
    ])
    // Ordenar por productId en el cliente
    stock.sort((a, b) => {
      const idA = (a.productId || '').toLowerCase()
      const idB = (b.productId || '').toLowerCase()
      const byId = idA.localeCompare(idB)
      if (byId !== 0) return byId
      const sizeA = (a.size || '').toString().toLowerCase()
      const sizeB = (b.size || '').toString().toLowerCase()
      const bySize = sizeA.localeCompare(sizeB)
      if (bySize !== 0) return bySize
      const colorA = (a.color || '').toString().toLowerCase()
      const colorB = (b.color || '').toString().toLowerCase()
      return colorA.localeCompare(colorB)
    })
    return stock
  } catch (error) {
    console.error('Error al obtener stock del depósito:', error)
    throw error
  }
}

/**
 * Obtener todo el stock (todos los productos en todos los depósitos)
 * @returns {Promise<Array>}
 */
export const getAllStock = async () => {
  try {
    return await getCollection(STOCK_COLLECTION, [], 'warehouseId', 'asc')
  } catch (error) {
    console.error('Error al obtener todo el stock:', error)
    throw error
  }
}

/**
 * Crear o actualizar stock
 * @param {string} productId
 * @param {string} warehouseId
 * @param {number} quantity
 * @param {number|null} minStock
 * @param {Object} options
 * @returns {Promise<string>}
 */
export const setStock = async (productId, warehouseId, quantity, minStock = null, options = {}) => {
  try {
    // Limpiar IDs
    const cleanProductId = (productId || '').trim()
    const cleanWarehouseId = (warehouseId || '').trim()
    const cleanSize = typeof options.size === 'string' ? options.size.trim() : options.size || null
    const cleanColor = typeof options.color === 'string' ? options.color.trim() : options.color || null

    if (!cleanProductId || !cleanWarehouseId) {
        throw new Error('ID de producto o depósito inválido')
    }

    // Verificar si ya existe stock para este producto en este depósito
    const existingStock = await getStock(cleanProductId, cleanWarehouseId, { size: cleanSize || undefined, color: cleanColor || undefined })
    
    const stockData = {
      productId: cleanProductId,
      warehouseId: cleanWarehouseId,
      quantity: Math.max(0, quantity), // No permitir cantidades negativas
      minStock: minStock !== null ? minStock : null,
      size: cleanSize || null,
      color: cleanColor || null,
      updatedAt: Timestamp.now()
    }
    
    if (existingStock) {
      // Actualizar stock existente
      await updateDocument(STOCK_COLLECTION, existingStock.id, stockData)
      
      // Sincronizar stock total en el producto
      await syncProductTotalStock(cleanProductId)
      
      return existingStock.id
    } else {
      // Crear nuevo registro de stock
      stockData.createdAt = Timestamp.now()
      const newId = await createDocument(STOCK_COLLECTION, stockData)
      
      // Sincronizar stock total en el producto
      await syncProductTotalStock(cleanProductId)
      
      return newId
    }
  } catch (error) {
    console.error('Error al establecer stock:', error)
    throw error
  }
}

/**
 * Sincroniza el stock total de un producto en su documento principal
 * @param {string} productId
 * @returns {Promise<number>}
 */
export const syncProductTotalStock = async (productId) => {
  try {
    // Obtener todo el stock de este producto
    const stockItems = await getProductStock(productId)
    
    // Calcular total
    const totalStock = stockItems.reduce((sum, item) => sum + (item.quantity || 0), 0)
    
    // Actualizar documento de producto
    // Nota: Usamos updateDocument de firestore.service que maneja la referencia a la colección 'products'
    await updateDocument('products', productId, { 
      stock: totalStock,
      // No actualizamos updatedAt para no alterar el ordenamiento por defecto si no es deseado
    })
    
    return totalStock
  } catch (error) {
    console.warn(`No se pudo sincronizar stock para producto ${productId} (posiblemente no existe):`, error)
    return 0
  }
}

/**
 * Repara IDs de stock y sincroniza totales
 * @returns {Promise<Object>}
 */
export const fixStockIdsAndSync = async () => {
  try {
    const allStock = await getAllStock()
    const allProducts = await getCollection('products')
    
    let fixedIds = 0
    let syncedProducts = 0
    const processedProductIds = new Set()
    
    // 1. Reparar IDs sucios en stock
    for (const item of allStock) {
      const currentId = item.productId
      const cleanId = (currentId || '').trim()
      
      // Si hay espacios, actualizar
      if (currentId !== cleanId) {
        await updateDocument(STOCK_COLLECTION, item.id, { productId: cleanId })
        fixedIds++
      }
      
      // Verificar si el producto existe
      const productExists = allProducts.some(p => p.id === cleanId)
      if (productExists) {
        processedProductIds.add(cleanId)
      }
    }
    
    // 2. Sincronizar stock para todos los productos encontrados en stock
    for (const productId of processedProductIds) {
      await syncProductTotalStock(productId)
      syncedProducts++
    }
    
    // 3. Poner stock 0 a productos que no tienen entradas en stock
    for (const product of allProducts) {
      if (!processedProductIds.has(product.id) && product.stock !== 0) {
        await updateDocument('products', product.id, { stock: 0 })
        syncedProducts++
      }
    }
    
    return { fixedIds, syncedProducts }
  } catch (error) {
    console.error('Error en reparación y sincronización de stock:', error)
    throw error
  }
}

/**
 * Registrar un movimiento de stock
 * @param {Object} movementData
 * @returns {Promise<string>}
 */
export const createStockMovement = async (movementData) => {
  try {
    const movement = {
      productId: (movementData.productId || '').trim(),
      warehouseId: (movementData.warehouseId || '').trim(),
      type: movementData.type, // 'entrada', 'salida', 'ajuste', 'transferencia'
      quantity: movementData.quantity,
      reason: movementData.reason || '',
      userId: movementData.userId || null,
      relatedMovementId: movementData.relatedMovementId || null, // Para transferencias
      size: movementData.size || null,
      color: movementData.color || null,
      createdAt: Timestamp.now()
    }
    
    return await createDocument(STOCK_MOVEMENTS_COLLECTION, movement)
  } catch (error) {
    console.error('Error al crear movimiento de stock:', error)
    throw error
  }
}

/**
 * Aplicar un movimiento de stock (actualiza el stock y registra el movimiento)
 * @param {Object} movementData
 * @returns {Promise<Object>}
 */
export const applyStockMovement = async (movementData) => {
  try {
    const { productId, warehouseId, type, quantity, reason, userId, size = null, color = null } = movementData
    
    const product = await getDocument('products', (productId || '').trim())
    if (!product) {
      throw new Error('Producto no encontrado')
    }
    const requiresColor = Array.isArray(product.colors) && product.colors.length > 0
    const requiresSize = Array.isArray(product.sizes) && product.sizes.length > 0
    const isFashion = product?.commercialProfile?.rubro === 'indumentaria'
    if (isFashion || requiresColor || requiresSize) {
      if (requiresColor && !color) throw new Error('Debe seleccionar color')
      if (requiresSize && !size) throw new Error('Debe seleccionar talle')
    }
    
    // Obtener stock actual
    const currentStock = await getStock(productId, warehouseId, { size, color })
    const currentQuantity = currentStock ? currentStock.quantity : 0
    
    // Calcular nueva cantidad según el tipo de movimiento
    let newQuantity = currentQuantity
    switch (type) {
      case 'entrada':
        newQuantity = currentQuantity + Math.abs(quantity)
        break
      case 'salida':
        newQuantity = Math.max(0, currentQuantity - Math.abs(quantity))
        break
      case 'ajuste':
        newQuantity = quantity // Ajuste establece la cantidad directamente
        break
      default:
        throw new Error(`Tipo de movimiento no válido: ${type}`)
    }
    
    // Actualizar stock
    const stockId = await setStock(productId, warehouseId, newQuantity, currentStock?.minStock, { size, color })
    
    // Registrar movimiento
    const movementId = await createStockMovement({
      productId,
      warehouseId,
      type,
      quantity: type === 'ajuste' ? newQuantity : Math.abs(quantity),
      reason,
      userId,
      size,
      color
    })
    
    return {
      stockId,
      movementId,
      previousQuantity: currentQuantity,
      newQuantity
    }
  } catch (error) {
    console.error('Error al aplicar movimiento de stock:', error)
    throw error
  }
}

/**
 * Transferir stock entre depósitos
 * @param {string} productId
 * @param {string} fromWarehouseId
 * @param {string} toWarehouseId
 * @param {number} quantity
 * @param {string} reason
 * @param {string} userId
 * @returns {Promise<Object>}
 */
export const transferStock = async (productId, fromWarehouseId, toWarehouseId, quantity, reason = '', userId = null) => {
  try {
    // Verificar stock disponible en origen
    const sourceStock = await getStock(productId, fromWarehouseId)
    if (!sourceStock || sourceStock.quantity < quantity) {
      throw new Error('Stock insuficiente en el depósito origen')
    }
    
    // Aplicar salida en origen
    const exitResult = await applyStockMovement({
      productId,
      warehouseId: fromWarehouseId,
      type: 'salida',
      quantity,
      reason: `Transferencia a ${toWarehouseId}: ${reason}`,
      userId
    })
    
    // Aplicar entrada en destino
    const entryResult = await applyStockMovement({
      productId,
      warehouseId: toWarehouseId,
      type: 'entrada',
      quantity,
      reason: `Transferencia desde ${fromWarehouseId}: ${reason}`,
      userId
    })
    
    // Vincular los movimientos
    await updateDocument(STOCK_MOVEMENTS_COLLECTION, exitResult.movementId, {
      relatedMovementId: entryResult.movementId
    })
    await updateDocument(STOCK_MOVEMENTS_COLLECTION, entryResult.movementId, {
      relatedMovementId: exitResult.movementId
    })
    
    return {
      exitMovementId: exitResult.movementId,
      entryMovementId: entryResult.movementId,
      fromQuantity: exitResult.newQuantity,
      toQuantity: entryResult.newQuantity
    }
  } catch (error) {
    console.error('Error al transferir stock:', error)
    throw error
  }
}

/**
 * Obtener movimientos de stock
 * @param {Object} filters
 * @returns {Promise<Array>}
 */
export const getStockMovements = async (filters = {}) => {
  try {
    const queryFilters = []
    
    if (filters.productId) {
      queryFilters.push({ field: 'productId', operator: '==', value: filters.productId })
    }
    
    if (filters.warehouseId) {
      queryFilters.push({ field: 'warehouseId', operator: '==', value: filters.warehouseId })
    }
    
    if (filters.type) {
      queryFilters.push({ field: 'type', operator: '==', value: filters.type })
    }
    
    if (filters.size) {
      queryFilters.push({ field: 'size', operator: '==', value: filters.size })
    }
    
    if (filters.color) {
      queryFilters.push({ field: 'color', operator: '==', value: filters.color })
    }
    
    // Obtener movimientos y ordenar en el cliente para evitar índices compuestos
    const movements = await getCollection(STOCK_MOVEMENTS_COLLECTION, queryFilters)
    
    // Ordenar por fecha de creación descendente
    movements.sort((a, b) => {
      const dateA = a.createdAt?.seconds || 0
      const dateB = b.createdAt?.seconds || 0
      return dateB - dateA // Descendente
    })
    
    return movements
  } catch (error) {
    console.error('Error al obtener movimientos de stock:', error)
    throw error
  }
}

/**
 * Obtener productos con stock bajo (alerta)
 * @param {string} warehouseId - ID del depósito (opcional, si no se proporciona busca en todos)
 * @returns {Promise<Array>} Lista de productos con stock bajo
 */
export const getLowStockProducts = async (warehouseId = null) => {
  try {
    const filters = warehouseId 
      ? [{ field: 'warehouseId', operator: '==', value: warehouseId }]
      : []
    
    const allStock = await getCollection(STOCK_COLLECTION, filters)
    
    // Filtrar productos donde quantity <= minStock
    return allStock.filter(stock => {
      if (stock.minStock === null || stock.minStock === undefined) {
        return false
      }
      return stock.quantity <= stock.minStock
    })
  } catch (error) {
    console.error('Error al obtener productos con stock bajo:', error)
    throw error
  }
}

